import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppLayout from "./AppLayout";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { IndianRupee } from "lucide-react";
import { Separator } from "./ui/separator";
import DateSlotModal from "./DateSlotModal";
import {
  ArrowLeft,
  Phone,
  MapPin,
  Clock,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import authService from "../Services/authService";

const AppointmentDetailsScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadAppointmentDataById();
  }, [id]);

  const loadAppointmentDataById = () => {
    const token = authService.getCurrentUsertoken();
    const url = new URL(
      authService.getApiUrl() + "phlebotomistorderid/" + id
    );

    fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=UTF-8",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (res.status === 401) {
          authService.clientlogout();
          window.location.href = "/login";
        } else {
          return res.json();
        }
      })
      .then((result) => {
        setData(result.data.orderdetails);
      })
      .catch((err) => {
        console.error("Error loading appointment:", err.message);
      });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Pending":
        return (
          <div className="flex items-center px-3 py-1 rounded-full bg-amber-100 text-amber-600 text-sm font-medium">
            <Clock className="h-4 w-4 mr-1" />
            Pending
          </div>
        );
      case "In-progress":
        return (
          <div className="flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
            <AlertCircle className="h-4 w-4 mr-1" />
            In Progress
          </div>
        );
      case "Completed":
        return (
          <div className="flex items-center px-3 py-1 rounded-full bg-green-100 text-green-600 text-sm font-medium">
            <CheckCircle className="h-4 w-4 mr-1" />
            Completed
          </div>
        );
      default:
        return null;
    }
  };

  if (!data) {
    return (
      <AppLayout>
        <p className="text-center text-gray-500">Loading...</p>
      </AppLayout>
    );
  }

  const formatFullAddress = (appt) => {
    const parts = [
      appt.building_name,
      appt.landmark,
      appt.area,
      appt.city,
      appt.state,
      appt.pincode
    ];

    return parts.filter(Boolean).join(", ").replace(/\s*,\s*/g, ", ").trim();
  };

  const handleNavigateLocation = (address) => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    // Show loading indicator or alert (optional)
    console.log("Attempting to get current location...");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        if (!latitude || !longitude) {
          alert("Failed to detect your location.");
          return;
        }

        const origin = `${latitude},${longitude}`;
        const destination = encodeURIComponent(address.trim());

        const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`;

        console.log("Opening Google Maps URL:", url);
        // ✅ Make sure it's allowed to open
        const newWindow = window.open(url, "_blank");
        if (!newWindow || newWindow.closed || typeof newWindow.closed === "undefined") {
          alert("Please allow popups to open Google Maps.");
        }
      },
      (error) => {
        console.error("Geolocation error:", error);

        let errorMessage = "Location error occurred.";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location permission was denied.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location position is unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out.";
            break;
        }

        alert(errorMessage + " Showing destination only.");

        // Fallback: open Maps without origin
        const destination = encodeURIComponent(address.trim());
        const fallbackUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
        window.open(fallbackUrl, "_blank");
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };



  return (
    <>
      <AppLayout hideFooter={isModalOpen}>
        <div className="flex items-center mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="mr-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="sr-only">Back</span>
          </Button>
          <h1 className="text-xl font-bold text-gray-800">Appointment Details</h1>
        </div>

        {Number(data.due_amount) > 0 && (
          <Card className="mb-4 border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Total Due Amount</h3>
                  </div>
                </div>
                <p className="text-lg font-bold text-pranaam-red">
                  ₹ {data.due_amount}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="mb-6 border-gray-200">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div className="text-left">
                <h3 className="text-sm font-medium text-gray-500 mb-1">
                  Date & Time
                </h3>
                <p className="flex items-center text-gray-800 pt-2">
                  <Clock className="h-4 w-4 mr-2 text-gray-500" />
                  {data.slot_date} at {data.slot_time}
                </p>
              </div>
              <div className="text-right">{getStatusBadge(data.order_status)}</div>
            </div>
          </CardHeader>

          <CardContent>
            <div className="space-y-4">
              <div className="text-left">
                <h3 className="text-sm font-medium text-gray-500 mb-1">Address</h3>
                <p className="flex items-start text-gray-800">
                  <MapPin className="h-4 w-4 mr-2 text-gray-500 mt-0.5" />
                  <span className="text-left">
                    {data.building_name}, {data.area}, {data.landmark}, {data.pincode}
                  </span>
                </p>
              </div>

              <div className="text-left">
                <h3 className="text-sm font-medium text-gray-500 mb-1">Phone Number</h3>
                <p className="flex items-start text-gray-800">
                  <Phone className="h-4 w-4 mr-2 text-gray-500 mt-0.5" />
                  <span>{data.phone_number}</span>
                </p>
              </div>

              <div className="flex space-x-3">
                <Button className="flex-1 bg-pranaam-red hover:bg-red-700 text-white" asChild>
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault(); // Prevent default anchor behavior
                      handleNavigateLocation(formatFullAddress(data));
                    }}
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    Navigate
                  </Link>
                </Button>

                <a href={`tel:+91${data.phone_number}`} className="flex-1">
                  <Button
                    variant="outline"
                    className="w-full border-gray-200 hover:bg-gray-50 text-gray-800"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Call Patient
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {data.orders_items?.map((appointment, index) => (
          <Card key={index} className="mb-6 border-gray-200">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <CardTitle className="text-lg text-gray-800">
                  {appointment.family_member_name}
                </CardTitle>
              </div>
              <Separator className="mt-2 mb-3 bg-gray-200" />
            </CardHeader>

            <CardContent className="pt-3">
              <ul className="space-y-2">
                {/* Direct tests */}
                {appointment.products?.map((product, idx) => (
                  <li key={`prod-${idx}`} className="flex items-center flex-nowrap text-gray-800">
                    <div className="h-5 w-5 flex-shrink-0 rounded-full border border-gray-300 mr-3"></div>
                    <span className="font-bold">{product.test_name}</span>
                  </li>
                ))}

                {/* Package names */}
                {appointment.packages?.map((pkg, pkgIdx) => (
                  <li key={`pkg-${pkgIdx}`} className="flex items-center flex-nowrap text-gray-800">
                    <div className="h-5 w-5 flex-shrink-0 rounded-full border border-gray-300 mr-3"></div>
                    <span className="font-bold">{pkg.test_name}</span>
                  </li>
                ))}

                {/* Nested package items */}
                {appointment.packages?.flatMap((pkg, i) =>
                  pkg.package_items.map((item, j) => (
                    <li key={`pkg-item-${i}-${j}`} className="pl-6 text-gray-800 text-sm leading-snug">
                      <span>({item.lab_test_name})</span>
                    </li>
                  ))
                )}
              </ul>
            </CardContent>

          </Card>
        ))}

        {data.order_status !== 'Completed' && (
          <div className="flex space-x-3">
            <Button
              variant="outline"
              className="flex-1 border-gray-200 hover:bg-gray-50 text-gray-800"
              onClick={() => setIsModalOpen(true)} // Show modal on click
            >
              Reschedule
            </Button>

            <Button className="flex-1 bg-pranaam-red hover:bg-red-700 text-white" asChild>
              <Link to={`/appointments/${id}/collect`}>Start Collection</Link>
            </Button>
          </div>
        )}


      </AppLayout>
      <DateSlotModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={(date, slot) => {
          console.log("Selected:", date, slot);
        }}
        orderId={id}
      />
    </>
  );
};

export default AppointmentDetailsScreen;
